<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useBookingsStore } from '../stores/bookings'
import EligibilityWizard, { type EligibilityResult } from '../components/EligibilityWizard.vue'

const authStore = useAuthStore()
const bookingsStore = useBookingsStore()
const router = useRouter()
const route = useRoute()

// Eligibility state
const showWizard = ref(true)
const eligibility = ref<EligibilityResult | null>(null)

const form = ref({
  check_in: '',
  check_out: '',
  check_in_time: '15:00',
  check_out_time: '11:00',
  title: '',
  num_guests: 1,
  notes: '',
  preheat_enabled: true,
  preheat_hours: 4,
})

const error = ref('')
const loading = ref(false)
const success = ref(false)

// Check for stored eligibility on mount
onMounted(() => {
  // Force wizard with ?wizard=true query param (for testing)
  const forceWizard = route.query.wizard === 'true'

  // Owners skip the wizard (unless forced)
  if (authStore.isOwner && !forceWizard) {
    showWizard.value = false
    eligibility.value = {
      allowedMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      seasonName: 'All Seasons (Owner)',
      restrictions: [],
    }
    initializeDates()
    return
  }

  // Check localStorage for cached eligibility (skip if forcing wizard)
  if (!forceWizard) {
    const cached = localStorage.getItem('cabin_eligibility')
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        // Check if cached within last 30 days
        if (parsed.timestamp && Date.now() - parsed.timestamp < 30 * 24 * 60 * 60 * 1000) {
          eligibility.value = parsed.result
          showWizard.value = false
          initializeDates()
          return
        }
      } catch (e) {
        localStorage.removeItem('cabin_eligibility')
      }
    }
  }
})

function initializeDates() {
  // Pre-fill check_in from URL
  const checkIn = route.query.check_in as string
  if (checkIn) {
    form.value.check_in = checkIn
    // Default check_out to day after check_in
    const date = new Date(checkIn)
    date.setDate(date.getDate() + 2)
    form.value.check_out = date.toISOString().split('T')[0]
  } else {
    // Find next available date based on eligibility
    const nextDate = findNextAvailableDate()
    form.value.check_in = nextDate.toISOString().split('T')[0]
    nextDate.setDate(nextDate.getDate() + 2)
    form.value.check_out = nextDate.toISOString().split('T')[0]
  }
}

function findNextAvailableDate(): Date {
  const today = new Date()
  today.setDate(today.getDate() + 1) // Start from tomorrow

  if (!eligibility.value) return today

  // Find the next date in an allowed month
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today)
    checkDate.setDate(checkDate.getDate() + i)
    const month = checkDate.getMonth() + 1 // 1-indexed
    if (eligibility.value.allowedMonths.includes(month)) {
      return checkDate
    }
  }

  return today
}

function onEligibilityComplete(result: EligibilityResult) {
  eligibility.value = result
  showWizard.value = false

  // Cache the result
  localStorage.setItem('cabin_eligibility', JSON.stringify({
    result,
    timestamp: Date.now(),
  }))

  initializeDates()
}

function resetEligibility() {
  localStorage.removeItem('cabin_eligibility')
  eligibility.value = null
  showWizard.value = true
}

// Validate selected dates against eligibility
const dateError = computed(() => {
  if (!eligibility.value || !form.value.check_in) return ''

  const checkInDate = new Date(form.value.check_in)
  const checkInMonth = checkInDate.getMonth() + 1

  if (!eligibility.value.allowedMonths.includes(checkInMonth)) {
    const monthName = checkInDate.toLocaleDateString('en-US', { month: 'long' })
    return `${monthName} is not available based on your eligibility. Your access: ${eligibility.value.seasonName}`
  }

  if (form.value.check_out) {
    const checkOutDate = new Date(form.value.check_out)
    const checkOutMonth = checkOutDate.getMonth() + 1

    if (!eligibility.value.allowedMonths.includes(checkOutMonth)) {
      const monthName = checkOutDate.toLocaleDateString('en-US', { month: 'long' })
      return `Check-out in ${monthName} is not available based on your eligibility.`
    }
  }

  return ''
})

// Generate min/max dates for date inputs
const minDate = computed(() => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
})

async function submitRequest() {
  error.value = ''

  // Validation
  if (!form.value.check_in || !form.value.check_out) {
    error.value = 'Please select check-in and check-out dates'
    return
  }

  if (dateError.value) {
    error.value = dateError.value
    return
  }

  if (new Date(form.value.check_in) >= new Date(form.value.check_out)) {
    error.value = 'Check-out must be after check-in'
    return
  }

  if (new Date(form.value.check_in) < new Date()) {
    error.value = 'Cannot book dates in the past'
    return
  }

  loading.value = true

  try {
    await bookingsStore.createBooking({
      check_in: form.value.check_in,
      check_out: form.value.check_out,
      check_in_time: form.value.check_in_time,
      check_out_time: form.value.check_out_time,
      title: form.value.title || undefined,
      num_guests: form.value.num_guests,
      notes: form.value.notes || undefined,
      preheat_enabled: form.value.preheat_enabled,
      preheat_hours: form.value.preheat_hours,
    })
    success.value = true
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to create booking'
  } finally {
    loading.value = false
  }
}

// Get month name helper
function getMonthNames(months: number[]): string {
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return months.map(m => names[m - 1]).join(', ')
}
</script>

<template>
  <div class="max-w-2xl mx-auto p-4 md:p-6">
    <!-- Eligibility Wizard -->
    <div v-if="showWizard">
      <EligibilityWizard @complete="onEligibilityComplete" />
    </div>

    <!-- Booking Form -->
    <div v-else class="card">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Book a Stay</h1>
      <p class="text-gray-600 mb-4">
        {{ authStore.canBook
          ? 'Your booking will be automatically confirmed.'
          : 'Your request will be sent to the owner for approval.'
        }}
      </p>

      <!-- Eligibility Summary -->
      <div v-if="eligibility && !authStore.isOwner" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div class="flex items-start justify-between">
          <div>
            <div class="font-medium text-blue-800">{{ eligibility.seasonName }}</div>
            <div class="text-sm text-blue-600 mt-1">
              Available months: {{ getMonthNames(eligibility.allowedMonths) }}
            </div>
            <ul v-if="eligibility.restrictions.length" class="text-sm text-blue-600 mt-2 list-disc list-inside">
              <li v-for="r in eligibility.restrictions" :key="r">{{ r }}</li>
            </ul>
          </div>
          <button
            @click="resetEligibility"
            class="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Retake quiz
          </button>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="success" class="text-center py-8">
        <div class="text-5xl mb-4">✅</div>
        <h2 class="text-xl font-bold text-gray-800 mb-2">
          {{ authStore.canBook ? 'Booking Confirmed!' : 'Request Submitted!' }}
        </h2>
        <p class="text-gray-600 mb-6">
          {{ authStore.canBook
            ? 'Your stay has been added to the calendar.'
            : 'The owner will review your request and get back to you.'
          }}
        </p>
        <router-link to="/" class="btn btn-primary">
          View Calendar
        </router-link>
      </div>

      <!-- Booking Form -->
      <form v-else @submit.prevent="submitRequest" class="space-y-6">
        <!-- Date Error Banner -->
        <div v-if="dateError" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="text-red-700">{{ dateError }}</p>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="check_in" class="form-label">Check-in Date</label>
            <input
              id="check_in"
              v-model="form.check_in"
              type="date"
              class="form-input"
              :min="minDate"
              required
            />
          </div>
          <div>
            <label for="check_out" class="form-label">Check-out Date</label>
            <input
              id="check_out"
              v-model="form.check_out"
              type="date"
              class="form-input"
              :min="form.check_in || minDate"
              required
            />
          </div>
        </div>

        <!-- Times -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="check_in_time" class="form-label">Check-in Time</label>
            <input
              id="check_in_time"
              v-model="form.check_in_time"
              type="time"
              class="form-input"
            />
          </div>
          <div>
            <label for="check_out_time" class="form-label">Check-out Time</label>
            <input
              id="check_out_time"
              v-model="form.check_out_time"
              type="time"
              class="form-input"
            />
          </div>
        </div>

        <!-- Title & Guests -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="title" class="form-label">Title (optional)</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              class="form-input"
              placeholder="e.g., Family Reunion"
            />
          </div>
          <div>
            <label for="num_guests" class="form-label">Number of Guests</label>
            <input
              id="num_guests"
              v-model.number="form.num_guests"
              type="number"
              min="1"
              max="20"
              class="form-input"
            />
          </div>
        </div>

        <!-- Notes -->
        <div>
          <label for="notes" class="form-label">Notes (optional)</label>
          <textarea
            id="notes"
            v-model="form.notes"
            class="form-input"
            rows="3"
            placeholder="Any special requests or information..."
          ></textarea>
        </div>

        <!-- Pre-heat Settings -->
        <div class="bg-gray-50 rounded-lg p-4">
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="form.preheat_enabled"
              type="checkbox"
              class="w-5 h-5 rounded border-gray-300"
            />
            <div>
              <div class="font-medium text-gray-800">Enable Pre-heating</div>
              <div class="text-sm text-gray-600">
                Automatically warm up the cabin before your arrival
              </div>
            </div>
          </label>

          <div v-if="form.preheat_enabled" class="mt-4">
            <label for="preheat_hours" class="form-label">Hours Before Arrival</label>
            <select id="preheat_hours" v-model.number="form.preheat_hours" class="form-input">
              <option :value="2">2 hours</option>
              <option :value="4">4 hours (recommended)</option>
              <option :value="6">6 hours (cold weather)</option>
              <option :value="8">8 hours (very cold)</option>
            </select>
          </div>
        </div>

        <!-- Error -->
        <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>

        <!-- Submit -->
        <div class="flex gap-4">
          <router-link to="/" class="btn btn-secondary flex-1 text-center">
            Cancel
          </router-link>
          <button
            type="submit"
            class="btn btn-primary flex-1"
            :disabled="loading || !!dateError"
          >
            {{ loading ? 'Submitting...' : (authStore.canBook ? 'Confirm Booking' : 'Submit Request') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
